import * as Popover from '@radix-ui/react-popover';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { TrackPerformanceItem } from '~/modules/label-perf/types';

interface ArtistLinkProps {
  artist: TrackPerformanceItem['artist'];
  relatedArtists: TrackPerformanceItem['relatedArtists'];
}

export function ArtistLink({ artist, relatedArtists }: ArtistLinkProps) {
  const { t } = useTranslation('label-perf');
  const [isPopoverOpen, setPopoverOpen] = useState(false);

  const allArtists =
    relatedArtists && relatedArtists.length > 0 ? relatedArtists : [artist];
  const validArtists = allArtists.filter((a) => a?.partyId);
  const artistCount = validArtists.length;

  if (artistCount === 0) {
    return <p className="truncate text-body-xs">{artist?.name || '--'}</p>;
  }

  const primaryArtist = validArtists[0];
  const remainingArtists = validArtists.slice(1);
  const remainingCount = remainingArtists.length;

  return (
    <div className="flex items-center gap-x-sm text-body-xs">
      <Link
        to={`/roster/${primaryArtist.partyId}/tracks`}
        className="truncate underline"
        title={primaryArtist.name}
      >
        {primaryArtist.name}
      </Link>

      {remainingCount > 0 && (
        <Popover.Root open={isPopoverOpen} onOpenChange={setPopoverOpen}>
          <Popover.Trigger asChild>
            <button
              type="button"
              className="flex cursor-pointer items-center whitespace-nowrap text-body-xs hover:underline"
              aria-label={`Show ${remainingCount} more artists`}
            >
              {t('body.moreWithCount', { count: remainingCount })}
            </button>
          </Popover.Trigger>

          <Popover.Portal>
            <Popover.Content
              className="z-50 w-[16em] rounded-sm border-px p-md shadow-lg bg-level-03 border-emphasis-subtle"
              side="bottom"
              align="start"
              sideOffset={8}
            >
              <div className="flex flex-col items-start">
                <span className="mb-sm text-body-sm font-bold text-default">
                  {t('body.additionalArtists')}
                </span>

                {remainingArtists.map((currentArtist) => (
                  <Link
                    key={currentArtist.partyId}
                    to={`/roster/${currentArtist.partyId}/tracks`}
                    className="mb-xs text-body-xs font-semibold underline text-default hover:text-subtle focus:outline-none"
                    onClick={() => setPopoverOpen(false)}
                  >
                    {currentArtist.name}
                  </Link>
                ))}
              </div>
              <Popover.Arrow />
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      )}
    </div>
  );
}

ArtistLink.displayName = 'ArtistLink';
